import { makeAutoObservable, runInAction } from 'mobx';
import { authApi, getToken, setToken, setUnauthorizedHandler } from '@/services/authApi';

/**
 * [2026-09-10] 웹 회원 인증 상태(MobX). 토큰=localStorage, 역할(role)로 관리자/일반 분기.
 *   단일세션: 다른 기기 로그인 시 이 기기 토큰이 401 → setUnauthorizedHandler로 자동 로그아웃.
 */
export interface AuthUser {
	userId: number;
	username: string | null;
	name: string;
	role: 'USER' | 'ADMIN';
	phone?: string;
	email?: string;
	socialProvider?: string | null;
	hasPassword?: boolean;   // [2026-09-11 #5] 비번 보유 여부 — 마이페이지 '변경' vs '설정' 분기
	retentionMonths?: number | null;   // [2026-09-11 #4] 개인정보 유효기간(개월, 0=탈퇴시까지). /me에서 제공
	status?: string;   // [2026-09-12] ACTIVE|PENDING|… — PENDING(소셜 완료대기)이면 전역 가드가 /auth/complete로 강제
}

export class AuthStore {
	user: AuthUser | null = null;
	ready = false;          // 초기 세션 복원 완료 여부(첫 me() 시도 후 true)
	sessionEndedMsg: string | null = null;  // 단일세션 만료 등 안내

	constructor() {
		makeAutoObservable(this);
		// 401(토큰 무효/단일세션 만료) → 자동 로그아웃 + 안내
		setUnauthorizedHandler(() => this.handleUnauthorized());
		this.restore();
	}

	get isAuthed() { return !!this.user; }
	get isAdmin() { return this.user?.role === 'ADMIN'; }

	/** 앱 시작 시 저장된 토큰으로 세션 복원. */
	async restore() {
		if (!getToken()) { runInAction(() => { this.ready = true; }); return; }
		const r = await authApi.me();
		runInAction(() => {
			if (r.success) this.user = toUser(r);
			else setToken(null);
			this.ready = true;
		});
	}

	/** @return null=성공, 아니면 오류메시지. */
	async login(loginId: string, password: string): Promise<string | null> {
		const r = await authApi.login(loginId, password);
		if (!r.success) return r.message || '로그인에 실패했습니다.';
		setToken(String(r.token));
		runInAction(() => { this.user = toUser(r); this.sessionEndedMsg = null; });
		return null;
	}

	/** 소셜 로그인 결과 적용(콜백 페이지에서 호출). @return null=성공, 아니면 오류메시지. */
	applySocialResult(r: { success?: boolean; message?: string; token?: unknown } & Record<string, unknown>): string | null {
		if (!r.success || !r.token) return r.message || '소셜 로그인에 실패했습니다.';
		setToken(String(r.token));
		runInAction(() => { this.user = toUser(r); this.sessionEndedMsg = null; });
		return null;
	}

	async logout() {
		try { await authApi.logout(); } catch { /* 무시 */ }
		setToken(null);
		runInAction(() => { this.user = null; });
	}

	private handleUnauthorized() {
		setToken(null);
		runInAction(() => {
			if (this.user) this.sessionEndedMsg = '다른 기기에서 로그인했거나 세션이 만료되었습니다. 다시 로그인해주세요.';
			this.user = null;
		});
	}

	clearSessionMsg() { this.sessionEndedMsg = null; }

	/** 탈퇴 완료 후 — 서버 호출 없이 로컬 세션만 정리(계정은 이미 서버에서 삭제됨). */
	clearSession() {
		setToken(null);
		runInAction(() => { this.user = null; this.sessionEndedMsg = null; });
	}
}

function toUser(r: Record<string, unknown>): AuthUser {
	return {
		userId: Number(r.userId),
		username: (r.username as string) ?? null,
		name: String(r.name ?? ''),
		role: (r.role === 'ADMIN' ? 'ADMIN' : 'USER'),
		phone: r.phone as string | undefined,
		email: r.email as string | undefined,
		socialProvider: (r.socialProvider as string | null) ?? null,
		// [2026-09-11 #5] login/social/me 응답 모두 hasPassword를 실어 보냄. undefined면 '있음'으로 안전 처리(자체계정 기본).
		hasPassword: r.hasPassword === undefined ? true : !!r.hasPassword,
		retentionMonths: r.retentionMonths == null ? null : Number(r.retentionMonths),
		// [2026-09-12] status 우선순위: /me는 status를 직접 실어줌 → 그대로. 소셜 콜백은 status 대신
		//   needsCompletion(boolean)만 주므로 true→'PENDING'. login/그 외(둘 다 없음)는 'ACTIVE'(로그인은 PENDING 제외).
		status: (r.status as string) ?? (r.needsCompletion ? 'PENDING' : 'ACTIVE'),
	};
}
